import '~/utils/dates.ts'; // loads dayjs and required plugins
import type { ReactNode } from 'react';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router-dom';

import CurrentDateProvider from '~/components/current-date-provider.tsx';
import { BugsnagErrorBoundary } from '~/utils/bugsnag.ts';

import type { Route } from './+types/root.ts';
import { AuthLayout } from './components/auth-layout.tsx';

import './index.css';

export const links: Route.LinksFunction = () => [];

export default function App() {
  return (
    <CurrentDateProvider>
      <Outlet />
    </CurrentDateProvider>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>Yzer Dashboard</title>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" href="/favicon.ico" />
        <Meta />
        <Links />
      </head>
      <body>
        <BugsnagErrorBoundary>{children}</BugsnagErrorBoundary>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <AuthLayout>
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        <p className="text-lg text-gray-600 font-medium">Loading…</p>
      </div>
    </AuthLayout>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
